import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Circle, Package, Truck, Wrench, Home, AlertCircle, Loader2, Wallet, RefreshCcw, MessageCircle, Phone, User, MapPin, CalendarDays, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Support contact — user-facing on the tracking page (2026-02-04).
const SUPPORT_WHATSAPP = "40750433328";  // international format for wa.me
const SUPPORT_PHONE_DISPLAY = "0750 433 328";
const SUPPORT_PHONE_TEL = "+40750433328";
const SUPPORT_WHATSAPP_PREFILL_MSG = (num) =>
  `Salut! Am o întrebare despre cererea #${num || ""}.`;

// Per-type timeline. Each step has a key + label + icon.
const FLOW_GARANTIE = [
  { key: "pending",         label: "Creată (Așteaptă AWB)",   icon: Circle },
  { key: "pickup_issued",   label: "Preluare Client",          icon: Package },
  { key: "in_transit",      label: "În drum spre service",     icon: Truck },
  { key: "at_service",      label: "Verificare Service",       icon: Wrench },
  { key: "delivery_issued", label: "În livrare către client",  icon: Truck },
  { key: "completed",       label: "Finalizată",               icon: Home },
];
const FLOW_RETUR = [
  { key: "pending",                     label: "Cerere Creată",                   icon: Circle },
  { key: "pickup_issued",               label: "Preluare Client",                 icon: Package },
  { key: "in_transit_warehouse",        label: "Către Depozit",                   icon: Truck },
  { key: "at_warehouse_pending_refund", label: "În verificare, Bani de returnat", icon: Wallet },
  { key: "completed",                   label: "Finalizată",                      icon: CheckCircle2 },
];
const FLOW_SCHIMB = [
  { key: "pending",              label: "În așteptare",                icon: Circle },
  { key: "outbound_in_delivery", label: "În livrare către client",    icon: Package },
  { key: "return_in_transit",    label: "În tranzit către depozit",   icon: RefreshCcw },
  { key: "completed",            label: "Finalizată",                 icon: Home },
];
const FLOW_LIVRARE = [
  { key: "pending",     label: "Cerere Livrare Creată", icon: Circle },
  { key: "in_delivery", label: "În drum spre client",   icon: Truck },
  { key: "completed",   label: "Finalizată",            icon: Home },
];
// Legacy → new key remap so old requests still render correctly on the public page.
const LEGACY_REMAP = {
  retur:  { at_service: "at_warehouse_pending_refund", refunded: "completed", refund_redeliver: "at_warehouse_pending_refund", awb_issued: "pickup_issued", verified: "at_warehouse_pending_refund" },
  schimb: { delivery_issued: "outbound_in_delivery", product_returned: "return_in_transit", outbound_delivered: "return_in_transit", awb_issued: "outbound_in_delivery", verified: "return_in_transit", refund_redeliver: "return_in_transit" },
  livrare: { awb_issued: "in_delivery", pickup_issued: "in_delivery", outbound_in_delivery: "in_delivery", delivery_issued: "in_delivery" },
  garantie: { awb_issued: "pickup_issued", verified: "at_service", refund_redeliver: "delivery_issued" },
};
function normalizeStatus(type, status) {
  return (LEGACY_REMAP[type] || {})[status] || status;
}
function pickFlow(type) {
  if (type === "retur") return FLOW_RETUR;
  if (type === "schimb") return FLOW_SCHIMB;
  if (type === "livrare") return FLOW_LIVRARE;
  return FLOW_GARANTIE;
}

export default function PublicTrack() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/returns/track/${token}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Eroare");
      }
      const d = await r.json();
      setData(d);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Se încarcă cererea...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-red-400 mb-3" />
          <h2 className="text-lg font-semibold mb-1">Cerere inexistentă</h2>
          <p className="text-sm text-slate-300">{error}</p>
          <p className="text-xs text-slate-500 mt-3">Verifică link-ul primit pe email.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Se încarcă cererea...
        </div>
      </div>
    );
  }

  const STATUS_FLOW = pickFlow(data.type);
  const normStatus = normalizeStatus(data.type, data.status);
  const currentIdx = STATUS_FLOW.findIndex((s) => s.key === normStatus);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur p-6 sm:p-8 shadow-2xl">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-medium mb-1">
                {data.type === "retur" ? "Cerere retur"
                 : data.type === "schimb" ? "Cerere schimb"
                 : "Cerere garanție"}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold">
                #{data.request_number}
              </h1>
              <div className="text-xs text-slate-400 mt-1">
                Creată la {data.created_at ? new Date(data.created_at).toLocaleDateString("ro-RO") : data.date}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Card label="Detalii Cerere">
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Nume Client</div>
                    <div data-testid="track-customer-name" className="text-sm font-medium truncate">{data.customer?.name || "—"}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Tel Client</div>
                    <div data-testid="track-customer-phone" className="text-sm">
                      {data.customer?.phone
                        ? <a href={`tel:${data.customer.phone}`} className="hover:text-emerald-300">{data.customer.phone}</a>
                        : "—"}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Data Cererii</div>
                    <div data-testid="track-request-date" className="text-sm">
                      {data.created_at ? new Date(data.created_at).toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" }) : (data.date || "—")}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Adresă</div>
                    <div data-testid="track-customer-address" className="text-sm">{data.customer?.address || "—"}</div>
                  </div>
                </div>
              </div>
            </Card>
            <Card label="Produs">
              <div className="flex items-center gap-3">
                {data.product?.image ? (
                  <img src={data.product.image} alt="" className="h-12 w-12 rounded-md object-cover bg-slate-800" />
                ) : (
                  <div className="h-12 w-12 rounded-md bg-slate-800 flex items-center justify-center">
                    <Package className="h-5 w-5 text-slate-500" />
                  </div>
                )}
                <div className="min-w-0">
                  <div data-testid="track-product-name" className="text-sm font-medium truncate">{data.product?.name || "—"}</div>
                  <div className="text-xs text-slate-400">
                    {data.product?.part_number ? `SKU: ${data.product.part_number}` : ""}
                    {data.product?.quantity > 1 ? ` · Buc: ${data.product.quantity}` : ""}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">Status</div>
            <ol className="relative space-y-3">
              {STATUS_FLOW.map((s, idx) => {
                const done = idx < currentIdx;
                const active = idx === currentIdx;
                const Icon = s.icon;
                return (
                  <li key={s.key} className="flex items-center gap-3">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-full border ${
                      done ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                      : active ? "bg-amber-500/20 border-amber-500/40 text-amber-300 animate-pulse"
                      : "bg-slate-800/60 border-slate-700/60 text-slate-500"
                    }`}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm ${active ? "text-amber-200 font-semibold" : done ? "text-emerald-200" : "text-slate-400"}`}>
                        {s.label}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {data.awbs?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-800">
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">AWB-uri</div>
              <div className="space-y-2">
                {data.awbs.map((a) => (
                  <div key={a.number} className="flex items-center justify-between text-sm rounded-lg bg-slate-800/40 px-3 py-2">
                    <div>
                      <div className="font-mono font-medium">{a.number}</div>
                      <div className="text-xs text-slate-400">
                        {a.kind === "pickup" ? "Ridicare de la tine" : a.kind === "delivery" ? "Livrare către tine" : a.kind}
                      </div>
                    </div>
                    <div className="text-xs text-slate-300">{a.status_text || a.status || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contactează Suportul — WhatsApp API + telefon */}
          <div className="mt-8 pt-6 border-t border-slate-800">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Contactează Suportul</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <a
                data-testid="track-contact-whatsapp"
                href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(SUPPORT_WHATSAPP_PREFILL_MSG(data.request_number))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 transition px-4 py-3 text-sm font-semibold text-white"
              >
                <MessageCircle className="h-5 w-5" />
                Trimite mesaj pe WhatsApp
              </a>
              <a
                data-testid="track-contact-phone"
                href={`tel:${SUPPORT_PHONE_TEL}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 transition px-4 py-3 text-sm font-semibold text-slate-100"
              >
                <Phone className="h-5 w-5 text-emerald-400" />
                Sună: {SUPPORT_PHONE_DISPLAY}
              </a>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800">
            <div className="flex items-start gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 shrink-0 text-slate-500 mt-0.5" />
              <p>
                Datele comenzii sunt vizibile doar prin acest link privat. <span className="text-slate-400">Nu îl distribui public.</span>
                <br />
                Această pagină se actualizează în timp real pe măsură ce cererea ta avansează.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ label, children }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">{label}</div>
      {children}
    </div>
  );
}
